// viewListModel — pure logic for the Views-sheet custom list (#86bb5dx62 / #86bb7phhh),
// extracted so the section grouping + permission gates are unit-tested independently of the
// React component.
//
// ROLE-AWARE (#86bb7phhh, dview/lite/docs/view-permission-model.md). The edit gates take a
// context object derived by the caller from Redux `state.user`:
//     ctx = { isAdmin: boolean, currentUserId: number }
//   • isAdmin       = !!state.user.fAdmin  (the ADMIN *right*, bit 2 — NOT bRole/superuser.
//                     Matches Java dview's AuthModel.checkHasRight(AccessRight.ADMIN).)
//   • currentUserId = state.user.bID       (the logged-in user's id; getSets rows carry bUser)
// Lite enforces the permission matrix CLIENT-SIDE regardless of what the server does (approved
// model). If server behavior ever differs, the matrix wins.
//
// Two concerns are deliberately DECOUPLED:
//   • CATEGORIZATION — which heading a view displays under. Mutually exclusive: every view lands
//     in EXACTLY ONE bucket (David 2026-08-15: "a view should only be surfaced in one of the
//     categories; no same view under two headings"). Enforced by `viewCategory` + a single-pass
//     classify in `categorizeViews`.
//   • GATING — whether this user may rename/delete/overwrite a view. Computed from the view's OWN
//     type flags + ownership, NEVER from which display bucket it happened to land in. So even a
//     pathological view carrying two type flags still gets the safe (most-restrictive) rule.

import { normalizeSearchText } from './searchText';

// A view is a "real" saved view (not a synthetic/computed placeholder shown to the user).
export const isRealView = (v) => !!v && !v.isSeparator && !String(v.sName || '').startsWith('__synthetic_');

// ── numeric coercion helpers ────────────────────────────────────────────────────────────────
// getSets rows / parsed view objects can carry ints, numeric strings, or booleans depending on
// the transport (JSON-RPC ints, eView parse, cloud synthesis). Normalize so the type predicates
// don't miss on a "0" vs 0 mismatch.
const asBool = (x) => x === true || x === 1 || x === '1';
const asNum = (x) => (x === null || x === undefined || x === '' ? NaN : Number(x));

// ── view-type classification (mutually exclusive; precedence Smart > Default > Global > User) ──
// Type flags a view object carries (set in actions/ViewActions.js during the getSets parse):
//   • isSmart  — an eView server-flagged smart view (fSmart===1) OR a cloud client-computed
//                aggregation. Read-only aggregation → never editable in place.
//   • fDefault — a system's default view (single-system).
//   • fGlobal / bUser===0 — shared-with-everyone. "global" is bUser===0 on the backend (there is
//                NO fGlobal column server-side), but lite's eView save path also stamps an
//                explicit fGlobal flag with bUser=caller (project_eview_view_types_and_editing),
//                so accept EITHER signal.
export const isSmartView = (v) => isRealView(v) && asBool(v.isSmart);
export const isDefaultView = (v) => isRealView(v) && !asBool(v.isSmart) && asBool(v.fDefault);
export const isGlobalView = (v) =>
  isRealView(v) && !asBool(v.isSmart) && !asBool(v.fDefault) && (asBool(v.fGlobal) || asNum(v.bUser) === 0);
export const isUserView = (v) =>
  isRealView(v) && !asBool(v.isSmart) && !asBool(v.fDefault) && !asBool(v.fGlobal) && asNum(v.bUser) !== 0;

// The single canonical bucket for a view: 'smart' | 'default' | 'global' | 'user'. Precedence
// makes the classification total + mutually exclusive.
export const viewCategory = (v) => {
  if (isSmartView(v)) return 'smart';
  if (isDefaultView(v)) return 'default';
  if (isGlobalView(v)) return 'global';
  return 'user';
};

// Ownership: a User view whose bUser matches the current user. When currentUserId is unknown
// (e.g. a product mode that doesn't populate bID) this is false — callers that need a privacy
// decision must handle the "unknown user" case explicitly (see categorizeViews).
export const isOwnUserView = (v, ctx) =>
  isUserView(v) && !!ctx && ctx.currentUserId != null && asNum(v.bUser) === asNum(ctx.currentUserId);

// ── edit gates (role-aware; decoupled from the display bucket) ────────────────────────────────

// Arrange (rearrange this view's cameras) is allowed on ANY real STORED view — INCLUDING Smart
// and Default (#86bb7phhh David + view-permission-model §B: the constraint moves to the SAVE —
// arranging a Smart/Default view can only be persisted as a NEW view via Save-As). Only
// auto-generated / computed placeholders are excluded: they regenerate, so arranging them is
// meaningless.
export const canArrangeView = (v) => isRealView(v) && !v.isGenerated;

// In-place modify (rename & save-over) AND delete share ONE matrix (view-permission-model §A):
//   Smart        → nobody
//   Default      → admin only
//   Global       → admin only
//   User (own)   → owner
//   User (other) → no  (also filtered out of the list entirely — never shown)
// Generated/synthetic placeholders are never modifiable (they regenerate).
const canModifyInPlace = (v, ctx) => {
  if (!isRealView(v) || v.isGenerated) return false;
  if (isSmartView(v)) return false; // Smart: never, for anyone
  if (isDefaultView(v) || isGlobalView(v)) return !!(ctx && ctx.isAdmin); // Default/Global: admin only
  return isOwnUserView(v, ctx); // User: owner only
};

// Rename/settings (the ⋯ "Rename & settings", which also flips the Default/Global flags).
export const canRenameView = (v, ctx) => canModifyInPlace(v, ctx);

// Overwrite (save-over an existing view). ONE source of truth shared by the Save modal AND the
// unsaved-changes quick-save, so they can't diverge.
export const canOverwriteView = (v, ctx) => canModifyInPlace(v, ctx);

// Delete. Same matrix as rename/overwrite. DELIBERATE DIVERGENCE FROM JAVA: Java dview lets an
// admin DELETE a Smart view (SetModel.checkSetIsDeletable) while blocking its Update; lite does
// NOT allow deleting a Smart view for ANYONE (view-permission-model §A — David #86bb7phhh).
export const canDeleteView = (v, ctx) => canModifyInPlace(v, ctx);

// Does a view expose any ⋯ row actions for this user? (If not, the ⋯ button is hidden entirely.)
export const hasRowActions = (v, ctx) => canRenameView(v, ctx) || canDeleteView(v, ctx);

// ── Save-As target gating (the Save-view modal) ───────────────────────────────────────────────
// Arrange/edit → Save-As a NEW view. The new view's TYPE is gated by role + cross-server span
// (view-permission-model §B):
//   User    → anyone (saved as their own user view)
//   Global  → admin only
//   Default → admin only AND single-system (never cross-server)
// Smart is never a Save-As target (server/cloud-derived, not client-created).
export const canSaveAsUser = () => true;
export const canSaveAsGlobal = (ctx) => !!(ctx && ctx.isAdmin);

// A view/arrangement is cross-server if its occupied slots span more than one distinct bSerial.
// Accepts the slot/cell array in whatever shape the caller has (each entry may carry bSerial or
// serial); empty/blank slots are ignored.
export const isCrossServerSlots = (slots) => {
  const serials = new Set();
  (slots || []).forEach((s) => {
    if (!s) return;
    // Empty-slot placeholders (fEnable:false — the documented empty marker, cameraCatalog.js /
    // Cell.js) have no real camera → no server, so they must NOT count toward "cross-server".
    // They serialize as bSerial:0 (and sometimes the current server), which otherwise reads as a
    // distinct server and falsely blocks the "Default" save target on a single-system view whose
    // grid simply isn't full. Guard bSerial===0 too (never a valid NVR serial). #86bb7phhh lite-test.
    if (s.fEnable === false) return;
    const serial = s.bSerial != null ? s.bSerial : s.serial;
    if (serial !== null && serial !== undefined && serial !== '' && serial !== 0) serials.add(String(serial));
  });
  return serials.size > 1;
};

// Default is admin-only AND single-system. `slots` is the current arrangement being saved.
export const canSaveAsDefault = (ctx, slots) => !!(ctx && ctx.isAdmin) && !isCrossServerSlots(slots);

// Case/accent/punctuation-insensitive name filter for the type-ahead Views search (so "rosas"
// matches "Rosa's Cafe" — David #86bb5dx62). Empty query → all.
export const searchViews = (views, query) => {
  const q = normalizeSearchText(query);
  if (!q) return views || [];
  return (views || []).filter((v) => v && v.sName && normalizeSearchText(v.sName).includes(q));
};

// Group the display views into Default / Global / Smart / User sections (order preserved),
// dropping empty sections. EACH view appears in EXACTLY ONE section (single-pass classify by
// viewCategory — David 2026-08-15: no view under two headings).
//
// USER-VIEW PRIVACY (HARD RULE, view-permission-model §Categorization): a User view is shown
// ONLY if it belongs to the current user; another user's User views are dropped entirely. This
// is defense-in-depth on top of the backend's own bUser scoping. Global / Default / Smart are
// shared and shown to everyone (including admins — admins do NOT see other users' private
// views). When currentUserId is unknown (ctx missing / no bID — e.g. a product mode that doesn't
// populate it) the client can't decide ownership, so it does NOT drop anything and relies on the
// server's scoping (never accidentally hiding the user's own views).
export const categorizeViews = (views, ctx) => {
  const buckets = { default: [], global: [], smart: [], user: [] };
  const knowUser = !!ctx && ctx.currentUserId != null;
  (views || []).filter(isRealView).forEach((v) => {
    const cat = viewCategory(v);
    // Privacy: drop a User view that POSITIVELY belongs to someone else (bUser present AND
    // different from the current user). If ownership can't be determined — no currentUserId, or
    // the view carries no bUser (cloud/legacy parse) — do NOT drop; the backend already scopes
    // getSets to the caller, so this never accidentally hides the user's own views.
    if (cat === 'user' && knowUser && v.bUser != null && asNum(v.bUser) !== asNum(ctx.currentUserId)) return;
    buckets[cat].push(v);
  });
  return [
    { key: 'default', label: 'Default Views', items: buckets.default },
    { key: 'global', label: 'Global Views', items: buckets.global },
    { key: 'smart', label: 'Smart Views', items: buckets.smart },
    { key: 'user', label: 'User Views', items: buckets.user },
  ].filter((s) => s.items.length > 0);
};
